import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  FileText, Download, Stethoscope, Calendar, Clock,
  CheckCircle, Loader2, Printer, Mail, ClipboardList
} from "lucide-react";

/**
 * Export to Doctor Feature
 * Formats chat history and symptom tracking into a professional PDF report
 * for clinical review by visiting nurse or physician
 */
export default function ExportToDoctor({ 
  chatHistory = [], 
  patientName = "", 
  patientId = "" 
}) {
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeSymptoms: true,
    includeMedications: true,
    includeChat: true,
    includeVitals: true,
    dateRange: "7days",
    additionalNotes: ""
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      // Generate clinical report
      const reportData = {
        patient_id: patientId,
        patient_name: patientName,
        generated_at: new Date().toISOString(),
        date_range: exportOptions.dateRange,
        include_symptoms: exportOptions.includeSymptoms,
        include_medications: exportOptions.includeMedications,
        include_chat: exportOptions.includeChat,
        include_vitals: exportOptions.includeVitals,
        additional_notes: exportOptions.additionalNotes,
        chat_history: exportOptions.includeChat ? chatHistory : []
      };

      // Call backend to generate PDF
      const response = await api.post("/caregiver/export-clinical-report", reportData);
      
      if (response.data?.html) {
        // Open in new window for printing
        const printWindow = window.open('', '_blank');
        printWindow.document.write(response.data.html);
        printWindow.document.close();
        
        toast.success("Clinical report generated! Use Print > Save as PDF");
      } else {
        // Fallback: Generate client-side
        const htmlReport = generateClinicalReportHTML(reportData);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlReport);
        printWindow.document.close();
        
        toast.success("Report generated successfully!");
      }

      setShowExport(false);
    } catch (error) {
      console.error("Export error:", error);
      // Use fallback
      const reportData = {
        patient_name: patientName || "Patient",
        generated_at: new Date().toISOString(),
        date_range: exportOptions.dateRange,
        additional_notes: exportOptions.additionalNotes,
        chat_history: exportOptions.includeChat ? chatHistory : []
      };
      const htmlReport = generateClinicalReportHTML(reportData);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlReport);
      printWindow.document.close();
      
      toast.success("Report generated!");
      setShowExport(false);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button 
        onClick={() => setShowExport(true)}
        variant="outline"
        className="gap-2"
        data-testid="export-to-doctor-btn"
      >
        <FileText className="w-4 h-4" />
        Export for Doctor
      </Button>

      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-[#4A7C6F]" />
              Export Clinical Report
            </DialogTitle>
            <DialogDescription>
              Generate a professional report for your visiting nurse or physician
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Date Range */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Time Period</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "3days", label: "3 Days" },
                  { value: "7days", label: "1 Week" },
                  { value: "14days", label: "2 Weeks" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setExportOptions(prev => ({ ...prev, dateRange: option.value }))}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      exportOptions.dateRange === option.value
                        ? "bg-[#4A7C6F] text-white border-[#4A7C6F]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#4A7C6F]"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Include Options */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Include in Report</Label>
              <div className="space-y-2">
                {[
                  { key: "includeSymptoms", label: "Symptom Tracking", icon: ClipboardList },
                  { key: "includeMedications", label: "Medication Log", icon: CheckCircle },
                  { key: "includeChat", label: "AI Chat History", icon: FileText },
                  { key: "includeVitals", label: "Vital Signs", icon: Calendar },
                ].map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      id={key}
                      checked={exportOptions[key]}
                      onCheckedChange={(checked) => 
                        setExportOptions(prev => ({ ...prev, [key]: checked }))
                      }
                    />
                    <Label htmlFor={key} className="flex items-center gap-2 cursor-pointer flex-1">
                      <Icon className="w-4 h-4 text-slate-500" />
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium">
                Additional Notes for Provider (Optional)
              </Label>
              <Textarea
                id="notes"
                value={exportOptions.additionalNotes}
                onChange={(e) => setExportOptions(prev => ({ ...prev, additionalNotes: e.target.value }))}
                placeholder="Any specific concerns or observations you want to highlight..."
                className="min-h-[80px]"
              />
            </div>

            {/* Preview Info */}
            <div className="p-4 bg-[#E8F4F0] rounded-lg">
              <h4 className="font-medium text-[#4A7C6F] mb-2">Report Preview</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Professional formatting for clinical review</li>
                <li>• Organized by date and category</li>
                <li>• Easy to print or share via email</li>
                <li>• HIPAA-compliant format</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExport(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport} 
              disabled={exporting}
              className="bg-[#4A7C6F] hover:bg-[#3D6B5F]"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Generate Clinical Report HTML
 * Creates a clean, professional PDF-ready report
 */
function generateClinicalReportHTML(data) {
  const {
    patient_name,
    generated_at,
    date_range,
    additional_notes,
    chat_history = []
  } = data;

  const dateRangeText = {
    "3days": "Past 3 Days",
    "7days": "Past Week",
    "14days": "Past 2 Weeks"
  }[date_range] || "Recent Period";

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Clinical Report - ${patient_name}</title>
  <style>
    @page { margin: 0.75in; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px;
    }
    .header {
      border-bottom: 3px solid #4A7C6F;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    .header h1 {
      color: #4A7C6F;
      margin: 0 0 5px 0;
      font-size: 28px;
    }
    .header h2 {
      color: #666;
      font-weight: normal;
      margin: 0;
      font-size: 16px;
    }
    .meta-info {
      display: flex;
      justify-content: space-between;
      margin-top: 15px;
      font-size: 13px;
      color: #666;
    }
    .section {
      margin-bottom: 30px;
      page-break-inside: avoid;
    }
    .section-title {
      background: #E8F4F0;
      color: #4A7C6F;
      padding: 10px 15px;
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 15px;
      border-left: 4px solid #4A7C6F;
    }
    .entry {
      padding: 12px 15px;
      margin-bottom: 10px;
      background: #f9f9f9;
      border-radius: 8px;
    }
    .entry-date {
      font-size: 12px;
      color: #888;
      margin-bottom: 5px;
    }
    .entry-role {
      font-weight: 600;
      color: #4A7C6F;
      margin-bottom: 5px;
    }
    .entry-content {
      color: #444;
    }
    .caregiver-notes {
      background: #FEF3C7;
      border: 1px solid #F59E0B;
      padding: 15px;
      border-radius: 8px;
      margin-bottom: 30px;
    }
    .caregiver-notes h3 {
      color: #92400E;
      margin-top: 0;
      font-size: 14px;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #ddd;
      font-size: 11px;
      color: #888;
      text-align: center;
    }
    .disclaimer {
      background: #F3F4F6;
      padding: 12px;
      border-radius: 6px;
      font-size: 11px;
      color: #666;
      margin-top: 30px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Caregiver Symptom & Care Report</h1>
    <h2>Clinical Review Document</h2>
    <div class="meta-info">
      <span><strong>Patient:</strong> ${patient_name}</span>
      <span><strong>Period:</strong> ${dateRangeText}</span>
      <span><strong>Generated:</strong> ${formatDate(generated_at)}</span>
    </div>
  </div>

  ${additional_notes ? `
  <div class="caregiver-notes">
    <h3>Caregiver Notes & Concerns</h3>
    <p>${additional_notes}</p>
  </div>
  ` : ''}

  <div class="section">
    <div class="section-title">AI Chat History - Symptom Discussions</div>
    ${chat_history.length > 0 ? chat_history.map(msg => `
      <div class="entry">
        <div class="entry-date">${formatDate(msg.timestamp || new Date().toISOString())}</div>
        <div class="entry-role">${msg.role === 'user' ? 'Caregiver' : 'AI Assistant'}</div>
        <div class="entry-content">${msg.content || msg.message || ''}</div>
      </div>
    `).join('') : `
      <div class="entry">
        <p style="color: #888; text-align: center;">No chat history available for this period</p>
      </div>
    `}
  </div>

  <div class="disclaimer">
    <strong>Disclaimer:</strong> This report was generated by the Goodwin Hospice caregiver support system.
    The AI assistant provides educational support and is not a substitute for professional medical evaluation.
    All clinical decisions should be made in consultation with the patient's healthcare team.
  </div>

  <div class="footer">
    <p>Goodwin Hospice • Northern Virginia's Premier Hospice Care</p>
    <p>Supporting Families with Dignity and Grace</p>
  </div>
</body>
</html>
  `;
}
